import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { ChevronRightIcon } from "../Pages/Auth/onboarding/icons";
import { RoutesPaths } from "../Router/constant";
import { getAccountUsage } from "../actions/account";
import { BILLING_BUSY_EVENT } from "../utils/billingEvents";

// The subscription rides along on the GET /auth/me record, which verifyToken()
// already puts in redux — so this reads from the store rather than refetching.
//
// That endpoint needs a session, so the exact field names couldn't be checked
// against a live response. Values are read through lists of likely aliases and
// anything missing is simply not rendered.

const firstOf = (source, keys) => {
  if (!source) return null;

  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }

  return null;
};

const titleCase = (value) =>
  String(value)
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

// Anything not obviously healthy gets the muted treatment.
const HEALTHY = ["active", "trialing", "trial"];

const CURRENCY_SYMBOLS = { USD: "$", EUR: "€", GBP: "£", INR: "₹" };

const formatPrice = (amount, currency) => {
  if (typeof amount !== "number") return null;

  // Stripe-style integers are minor units; 4900 means $49.
  const value = Number.isInteger(amount) && amount >= 1000 ? amount / 100 : amount;
  const symbol = CURRENCY_SYMBOLS[String(currency || "USD").toUpperCase()] || "";

  return `${symbol}${value % 1 === 0 ? value : value.toFixed(2)}`;
};

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
};

export const readSubscription = (subscription) => {
  if (!subscription) return null;

  const usage = firstOf(subscription, ["usage", "messages", "quota"]) || subscription;

  const used = firstOf(usage, [
    "messagesUsed",
    "used",
    "messageCount",
    "current",
  ]);
  const limit = firstOf(usage, [
    "messagesLimit",
    "limit",
    "messageLimit",
    "quota",
    "included",
  ]);

  const status = firstOf(subscription, ["status", "state"]);
  const interval = firstOf(subscription, [
    "interval",
    "billingInterval",
    "period",
    "billingPeriod",
  ]);

  const maxAssistants = firstOf(subscription, [
    "maxAssistants",
    "assistantLimit",
  ]);
  const chatLimit = firstOf(subscription, [
    "monthlyChatLimitPerAssistant",
    "chatLimitPerAssistant",
  ]);

  return {
    plan:
      firstOf(subscription, ["plan", "planName", "name", "tier", "product"]) ||
      "Free plan",
    status: status ? titleCase(status) : null,
    healthy: HEALTHY.includes(String(status || "").toLowerCase()),
    price: formatPrice(
      firstOf(subscription, ["price", "amount", "monthlyPrice", "unitAmount"]),
      firstOf(subscription, ["currency"]),
    ),
    interval: interval ? String(interval).replace(/ly$/, "") : null,
    used: typeof used === "number" ? used : null,
    limit: typeof limit === "number" ? limit : null,
    maxAssistants: typeof maxAssistants === "number" ? maxAssistants : null,
    chatLimit: typeof chatLimit === "number" ? chatLimit : null,
    // When the plan started, as opposed to when it next renews.
    startedOn: formatDate(firstOf(subscription, ["createdAt", "startedAt"])),
    renewsOn: formatDate(
      firstOf(subscription, [
        "renewsAt",
        "currentPeriodEnd",
        "nextBillingDate",
        "renewalDate",
        "expiresAt",
        "trialEndsAt",
      ]),
    ),
    cancelling: Boolean(
      firstOf(subscription, ["cancelAtPeriodEnd", "cancelling", "canceled"]),
    ),
  };
};

// GET /account/usage reports used-vs-limit per resource. The rows are listed
// here so the card keeps a stable order and readable labels; the numbers are
// entirely the endpoint's.
const USAGE_ROWS = [
  { key: "assistants", label: "Assistants" },
  { key: "conversations", label: "Conversations" },
  { key: "leads", label: "Leads" },
  { key: "knowledge", label: "Knowledge sources" },
];

const readMeters = (usage) =>
  USAGE_ROWS.map(({ key, label }) => {
    const row = usage?.[key];
    if (!row || typeof row.used !== "number") return null;

    const limit = typeof row.limit === "number" ? row.limit : null;
    // -1 is the API's "no cap", which is a count rather than a meter.
    const unlimited = limit === -1;
    const percent =
      unlimited || !limit ? 0 : Math.round((row.used / limit) * 100);

    return {
      key,
      label,
      used: row.used,
      limit,
      unlimited,
      percent,
      // Over quota is a real state here - the account is on 8 assistants
      // against a limit of 3 - so it gets its own treatment rather than a
      // bar that silently stops at full.
      over: !unlimited && limit > 0 && row.used > limit,
    };
  }).filter(Boolean);

export default function SubscriptionCard({ subscription, assistantCount = 0 }) {
  const dispatch = useDispatch();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  // A payment is being applied right now. The plan on screen is the one from
  // before it, so the card waits rather than showing figures it is about to
  // contradict.
  const [settling, setSettling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);

    const result = await dispatch(getAccountUsage());

    setLoading(false);
    if (result?.success) setUsage(result.usage);
  }, [dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  // The allowances come from the plan, so a payment changes them - and this
  // card is mounted on every page, including the one the payment lands on.
  useEffect(() => {
    const onBusy = async (event) => {
      if (event.detail?.busy) {
        setSettling(true);
        return;
      }

      // Settled: the plan behind these numbers is a different one now. The
      // skeleton is held until the new ones are in, rather than flashing the
      // old figures in between.
      await load();
      setSettling(false);
    };

    window.addEventListener(BILLING_BUSY_EVENT, onBusy);
    return () => window.removeEventListener(BILLING_BUSY_EVENT, onBusy);
  }, [load]);

  const meters = readMeters(usage);
  const plan = readSubscription(subscription);

  // Nothing is known yet, or what is known is about to be replaced: the
  // session record is still loading, or a payment is being applied. Claiming
  // "Free plan" here would be a guess that turns out wrong for every paying
  // account, and showing the old plan through a payment is worse.
  if (settling || (loading && !plan)) {
    return (
      <div className="dash-sidebar__plan" aria-busy="true">
        <div className="dash-sidebar__plan-top">
          <span className="skel dash-sidebar__skel--name" />
          <span className="skel dash-sidebar__skel--status" />
        </div>

        <span className="skel dash-sidebar__skel--price" />

        <ul className="dash-sidebar__meters">
          {[0, 1, 2].map((row) => (
            <li key={row}>
              <div className="dash-sidebar__plan-usage">
                <span className="skel dash-sidebar__skel--label" />
                <span className="skel dash-sidebar__skel--count" />
              </div>
              <span className="skel dash-sidebar__skel--bar" />
            </li>
          ))}
        </ul>

        <span className="skel dash-sidebar__skel--btn" />
      </div>
    );
  }

  // No subscription on the account yet — point at billing rather than render
  // an empty shell or invent a plan.
  if (!plan) {
    return (
      <div className="dash-sidebar__plan">
        <div className="dash-sidebar__plan-top">
          <span className="dash-sidebar__plan-name">Free plan</span>
        </div>
        <p className="dash-sidebar__plan-renew">
          Upgrade to unlock more messages and features.
        </p>
        <Link to={RoutesPaths.BILLING} className="dash-sidebar__plan-btn">
          View plans
          <ChevronRightIcon />
        </Link>
      </div>
    );
  }

  return (
    <div className="dash-sidebar__plan">
      <div className="dash-sidebar__plan-top">
        <span className="dash-sidebar__plan-name">{plan.plan}</span>
        {plan.status && (
          <span
            className={`dash-sidebar__plan-status${plan.healthy ? "" : " is-muted"}`}
          >
            {plan.status}
          </span>
        )}
      </div>

      {plan.price && (
        <div className="dash-sidebar__plan-price">
          <strong>{plan.price}</strong>
          {plan.interval && <span>/ {plan.interval}</span>}
        </div>
      )}

      {loading && meters.length === 0 && (
        <ul className="dash-sidebar__meters">
          {[0, 1, 2].map((row) => (
            <li key={row}>
              <div className="dash-sidebar__plan-usage">
                <span className="skel dash-sidebar__skel--label" />
                <span className="skel dash-sidebar__skel--count" />
              </div>
              <span className="skel dash-sidebar__skel--bar" />
            </li>
          ))}
        </ul>
      )}

      {meters.length > 0 && (
        <ul className="dash-sidebar__meters">
          {meters.map((meter) => (
            <li key={meter.key}>
              <div className="dash-sidebar__plan-usage">
                <span>{meter.label}</span>
                <strong className={meter.over ? "is-over" : undefined}>
                  {meter.used.toLocaleString()}
                  {meter.unlimited
                    ? " / ∞"
                    : meter.limit
                      ? ` / ${meter.limit.toLocaleString()}`
                      : ""}
                </strong>
              </div>

              {!meter.unlimited && Boolean(meter.limit) && (
                <div className="dash-sidebar__plan-bar">
                  {/* Clamped so an over-quota account fills the track rather
                      than painting past it. */}
                  <span
                    style={{ width: `${Math.min(100, meter.percent)}%` }}
                    className={
                      meter.over
                        ? "is-over"
                        : meter.percent >= 90
                          ? "is-high"
                          : undefined
                    }
                  />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {meters.some((meter) => meter.over) && (
        <p className="dash-sidebar__plan-over">
          You are over your plan limit.
        </p>
      )}
      {/* {(plan.renewsOn || plan.startedOn) && (
        <p className="dash-sidebar__plan-renew">
          {plan.renewsOn
            ? `${plan.cancelling ? "Ends on" : "Renews on"} ${plan.renewsOn}`
            : `Member since ${plan.startedOn}`}
        </p>
      )} */}

      <Link to={RoutesPaths.BILLING} className="dash-sidebar__plan-btn">
        Manage plan
        <ChevronRightIcon />
      </Link>
    </div>
  );
}
